import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/components/Layout";
import DashboardLayout from "@/components/DashboardLayout";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsNew from "@/components/WhatsNew";
import ReinstallForIcon from "@/components/ReinstallForIcon";
import PushReinstallNudge from "@/components/PushReinstallNudge";
import ScrollToTop from "@/components/ScrollToTop";

// ─── EAGER-LOADED (SEO-critical / high-traffic public pages) ─────────────
import Home from "@/pages/Home";
import FanFest from "@/pages/FanFest";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import Directory from "@/pages/Directory";
import BusinessProfile from "@/pages/BusinessProfile";
import Prizes from "@/pages/Prizes";
import SponsorInfo from "@/pages/SponsorInfo";
import FAQ from "@/pages/FAQ";
import Leaderboard from "@/pages/Leaderboard";

// ─── LAZY-LOADED (behind auth or secondary) ──────────────────────────────
// Secondary public pages (rare traffic, not primary landing pages)
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword  = lazy(() => import("@/pages/ResetPassword"));
const Rules          = lazy(() => import("@/pages/Rules"));
const Privacy        = lazy(() => import("@/pages/Privacy"));

// Consumer dashboard
const ConsumerDash   = lazy(() => import("@/pages/consumer/Dashboard"));
const Passport       = lazy(() => import("@/pages/consumer/Passport"));
const CheckInPage    = lazy(() => import("@/pages/consumer/CheckIn"));
const Offers         = lazy(() => import("@/pages/consumer/Offers"));
const MyPrizes       = lazy(() => import("@/pages/consumer/MyPrizes"));
const Profile        = lazy(() => import("@/pages/consumer/Profile"));

// Business dashboard
const BusinessDash        = lazy(() => import("@/pages/business/Dashboard"));
const BusinessProfileMgr  = lazy(() => import("@/pages/business/ProfileMgr"));
const BusinessOffers      = lazy(() => import("@/pages/business/Offers"));
const BusinessEvents      = lazy(() => import("@/pages/business/Events"));
const BusinessQR          = lazy(() => import("@/pages/business/QRCenter"));
const BusinessLeads       = lazy(() => import("@/pages/business/Leads"));
const BusinessAnalytics   = lazy(() => import("@/pages/business/Analytics"));
const BusinessBoostShop   = lazy(() => import("@/pages/business/BoostShop"));
const BoostSuccess        = lazy(() => import("@/pages/business/BoostSuccess"));
const CheckoutSuccess     = lazy(() => import("@/pages/business/CheckoutSuccess"));

// Admin dashboard (biggest bundle — 14 pages)
const AdminDash            = lazy(() => import("@/pages/admin/Dashboard"));
const AdminUsers           = lazy(() => import("@/pages/admin/Users"));
const AdminBusinesses      = lazy(() => import("@/pages/admin/Businesses"));
const AdminSweepstakes     = lazy(() => import("@/pages/admin/Sweepstakes"));
const AdminPrizes          = lazy(() => import("@/pages/admin/PrizesMgr"));
const AdminLivestream      = lazy(() => import("@/pages/admin/Livestream"));
const AdminAudit           = lazy(() => import("@/pages/admin/AuditLog"));
const AdminBroadcast       = lazy(() => import("@/pages/admin/Broadcast"));
const AdminSignIns         = lazy(() => import("@/pages/admin/SignIns"));
const AdminScheduled       = lazy(() => import("@/pages/admin/ScheduledBroadcasts"));
const AdminWinnerClaims    = lazy(() => import("@/pages/admin/WinnerClaims"));
const AdminStreakBoosts    = lazy(() => import("@/pages/admin/StreakBoosts"));
const AdminPushLogs        = lazy(() => import("@/pages/admin/PushLogs"));
const AdminBrandManager    = lazy(() => import("@/pages/admin/BrandManager"));

// Chat
const Messages = lazy(() => import("@/pages/chat/Messages"));

const publicPage = (el) => <PublicLayout>{el}</PublicLayout>;
const dashPage = (roles, el) => (
  <ProtectedRoute roles={roles}>
    <DashboardLayout>{el}</DashboardLayout>
  </ProtectedRoute>
);

const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    className="min-h-[50vh] flex items-center justify-center text-white/50 text-sm"
    data-testid="route-loading"
  >
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
      Loading…
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster theme="dark" position="top-right" richColors />
        <InstallPrompt />
        <WhatsNew />
        <ReinstallForIcon />
        <PushReinstallNudge />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={publicPage(<Home />)} />
          <Route path="/fanfest" element={publicPage(<FanFest />)} />
          <Route path="/register" element={publicPage(<Register />)} />
          <Route path="/login" element={publicPage(<Login />)} />
          <Route path="/forgot-password" element={publicPage(<ForgotPassword />)} />
          <Route path="/reset-password" element={publicPage(<ResetPassword />)} />
          <Route path="/directory" element={publicPage(<Directory />)} />
          <Route path="/business/:id" element={publicPage(<BusinessProfile />)} />
          <Route path="/prizes" element={publicPage(<Prizes />)} />
          <Route path="/rules" element={publicPage(<Rules />)} />
          <Route path="/privacy" element={publicPage(<Privacy />)} />
          <Route path="/sponsors" element={publicPage(<SponsorInfo />)} />
          <Route path="/faq" element={publicPage(<FAQ />)} />
          <Route path="/leaderboard" element={publicPage(<Leaderboard />)} />
          <Route path="/checkin/:id" element={publicPage(<BusinessProfile checkinMode />)} />

          {/* Consumer */}
          <Route path="/dashboard" element={dashPage(["consumer","admin"], <ConsumerDash />)} />
          <Route path="/dashboard/passport" element={dashPage(["consumer","admin"], <Passport />)} />
          <Route path="/dashboard/checkin" element={dashPage(["consumer","admin"], <CheckInPage />)} />
          <Route path="/dashboard/offers" element={dashPage(["consumer","admin"], <Offers />)} />
          <Route path="/dashboard/prizes" element={dashPage(["consumer","admin"], <MyPrizes />)} />
          <Route path="/dashboard/profile" element={dashPage(["consumer","admin"], <Profile />)} />
          <Route path="/dashboard/messages" element={dashPage(["consumer","admin"], <Messages />)} />

          {/* Business */}
          <Route path="/business" element={dashPage(["business","admin"], <BusinessDash />)} />
          <Route path="/business/messages" element={dashPage(["business","admin"], <Messages />)} />
          <Route path="/business/profile" element={dashPage(["business","admin"], <BusinessProfileMgr />)} />
          <Route path="/business/offers" element={dashPage(["business","admin"], <BusinessOffers />)} />
          <Route path="/business/events" element={dashPage(["business","admin"], <BusinessEvents />)} />
          <Route path="/business/qr" element={dashPage(["business","admin"], <BusinessQR />)} />
          <Route path="/business/leads" element={dashPage(["business","admin"], <BusinessLeads />)} />
          <Route path="/business/analytics" element={dashPage(["business","admin"], <BusinessAnalytics />)} />
          <Route path="/business/boost" element={dashPage(["business","admin"], <BusinessBoostShop />)} />
          <Route path="/business/boost/success" element={dashPage(["business","admin"], <BoostSuccess />)} />
          <Route path="/business/checkout/success" element={publicPage(<CheckoutSuccess />)} />

          {/* Admin */}
          <Route path="/admin" element={dashPage(["admin"], <AdminDash />)} />
          <Route path="/admin/messages" element={dashPage(["admin"], <Messages />)} />
          <Route path="/admin/users" element={dashPage(["admin"], <AdminUsers />)} />
          <Route path="/admin/businesses" element={dashPage(["admin"], <AdminBusinesses />)} />
          <Route path="/admin/sweepstakes" element={dashPage(["admin"], <AdminSweepstakes />)} />
          <Route path="/admin/prizes" element={dashPage(["admin"], <AdminPrizes />)} />
          <Route path="/admin/livestream" element={dashPage(["admin"], <AdminLivestream />)} />
          <Route path="/admin/notifications" element={dashPage(["admin"], <AdminBroadcast />)} />
          <Route path="/admin/push-logs" element={dashPage(["admin"], <AdminPushLogs />)} />
          <Route path="/admin/brand" element={dashPage(["admin"], <AdminBrandManager />)} />
          <Route path="/admin/audit" element={dashPage(["admin"], <AdminAudit />)} />
          <Route path="/admin/sign-ins" element={dashPage(["admin"], <AdminSignIns />)} />
          <Route path="/admin/scheduled" element={dashPage(["admin"], <AdminScheduled />)} />
          <Route path="/admin/winner-claims" element={dashPage(["admin"], <AdminWinnerClaims />)} />
          <Route path="/admin/streak-boosts" element={dashPage(["admin"], <AdminStreakBoosts />)} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
